import React, { useEffect, useState, useCallback } from 'react';
import friendlyError from '../../utils/friendlyError';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import {
  FaHeartbeat, FaSyncAlt, FaArrowUp, FaArrowDown, FaExclamationTriangle,
  FaBalanceScale, FaLink, FaComments, FaHashtag, FaHeart, FaBullhorn, FaTheaterMasks, FaFileAlt,
} from 'react-icons/fa';
import {
  TechShell, TechPageHeader, TechSection, StatStrip, StatTile, StatLabel, StatValue, StatSub,
  TechButtonGhost, Empty, Loading, ErrorBox, borderSoft, mono, accent,
} from '../../styles/techComponents';
import { getReputationSeries } from '../../services/reputationService';

function BreakTile({ label, value, sub }) {
  return (
    <div style={{ border: `1px solid ${borderSoft}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#e2e8f0', marginTop: 2 }}>{value}</div>
      {sub ? <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
}

function SentTag({ s }) {
  const map = {
    pos: { t: 'A FAVOR', c: '#22c55e', b: 'rgba(34,197,94,0.12)' },
    neg: { t: 'EN CONTRA', c: '#ef4444', b: 'rgba(239,68,68,0.12)' },
    neu: { t: 'NEUTRO', c: '#94a3b8', b: 'rgba(148,163,184,0.12)' },
  };
  const m = map[s] || map.neu;
  return (
    <span style={{ color: m.c, background: m.b, fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 6, whiteSpace: 'nowrap', flexShrink: 0 }}>{m.t}</span>
  );
}

function Bar({ label, value, max, color, right }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
      <div style={{ width: 150, fontSize: 12.5, color: '#cbd5e1', flexShrink: 0, textTransform: 'capitalize' }}>{label}</div>
      <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: 6, height: 14, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color || accent, transition: 'width .3s' }} />
      </div>
      <div style={{ width: 54, textAlign: 'right', fontSize: 12, color: '#94a3b8', fontFamily: mono }}>{right ?? value}</div>
    </div>
  );
}

const leanColor = (lean) => (lean === 'favor' ? '#22c55e' : lean === 'contra' ? '#ef4444' : '#eab308');

function ArgList({ title, color, items }) {
  return (
    <div style={{ flex: 1, minWidth: 260, border: `1px solid ${borderSoft}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ color, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{title}</div>
      {items?.length ? (
        <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((t, i) => <li key={i} style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.4 }}>{t}</li>)}
        </ul>
      ) : <div style={{ color: '#64748b', fontSize: 12 }}>—</div>}
    </div>
  );
}

export default function Reputacion() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await getReputationSeries()); }
    catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <TechShell><Loading>Cargando índice…</Loading></TechShell>;
  if (error) return <TechShell><ErrorBox><FaExclamationTriangle /> {error}</ErrorBox></TechShell>;

  const serie = (data?.serie || []).map((d) => ({ ...d, dia: String(d.fecha).slice(5) }));
  const actual = data?.actual;
  const delta = data?.delta || 0;
  const detalle = data?.detalle;
  const brk = detalle?.breakdown;
  const fuentes = detalle?.fuentes || [];
  const comentarios = detalle?.comentarios || [];
  const stats = detalle?.stats;
  const temasMax = Math.max(1, ...(stats?.temas || []).map((t) => t.menciones || 0));

  return (
    <TechShell>
      <TechPageHeader
        title="Índice de Reputación"
        dossier="LONGITUDINAL"
        subtitle="El humor sobre Chouhy medido sobre miles de comentarios REALES en publicaciones de redes que lo mencionan (posteos suyos + medios). Encuadre político: atacar al rival cuenta a favor. Con análisis de temas, argumentos de cada bando y emociones."
        actions={<TechButtonGhost onClick={load}><FaSyncAlt /> Actualizar</TechButtonGhost>}
      />
      <StatStrip>
        <StatTile><StatLabel>Índice hoy</StatLabel><StatValue>{actual ? actual.score : '—'}</StatValue><StatSub>{delta >= 0 ? <><FaArrowUp style={{ color: '#22c55e' }} /> +{delta}</> : <><FaArrowDown style={{ color: '#ef4444' }} /> {delta}</>} vs día previo</StatSub></StatTile>
        <StatTile><StatLabel>Comentarios (redes)</StatLabel><StatValue>{actual?.menciones ?? '—'}</StatValue><StatSub>{detalle?.posts_analizados ?? 0} posteos que mencionan a Chouhy</StatSub></StatTile>
        <StatTile><StatLabel>Share of voice</StatLabel><StatValue>{actual?.sov_pct ?? '—'}%</StatValue><StatSub>vs Passaglia</StatSub></StatTile>
        <StatTile><StatLabel>Sentimiento</StatLabel><StatValue style={{ color: '#22c55e' }}>{actual?.positivas ?? 0}</StatValue><StatSub><span style={{ color: '#ef4444' }}>{actual?.negativas ?? 0} neg</span> · {actual?.neutras ?? 0} neu</StatSub></StatTile>
      </StatStrip>

      <TechSection title="De dónde salen estos datos (todo real)" icon={<FaFileAlt />}>
        {actual ? (
          <div style={{ fontSize: 13.5, color: '#cbd5e1', lineHeight: 1.75 }}>
            <div>📅 <b>Último análisis:</b> {actual?.fecha ? new Date(`${String(actual.fecha).slice(0, 10)}T00:00`).toLocaleDateString('es-AR') : '—'}</div>
            <div>🔎 <b>Fuente:</b> {detalle?.fuente === 'social'
              ? 'Comentarios REALES de redes, del monitoreo de las publicaciones que mencionan a Chouhy.'
              : 'Sentimiento de prensa (alternativa): no hubo comentarios de redes en esta medición — posteos sin comentarios públicos disponibles.'}</div>
            <div>📊 <b>Base del análisis:</b> {detalle?.posts_analizados ?? 0} posteos que mencionan a Chouhy (los suyos + medios locales) · {detalle?.comentarios_reales ?? actual?.menciones ?? 0} comentarios reales · {detalle?.comentarios_clasificados ?? 0} clasificados uno por uno con IA.</div>
            <div>🧮 <b>Cómo se calcula:</b> cada comentario se etiqueta a favor / en contra / neutro con IA; el índice = sentimiento × 0.7 + share of voice vs Passaglia × 0.3. <b>Nunca se fabrican métricas</b>: si no hay comentarios reales, se dice explícitamente y se usa la alternativa de prensa.</div>
            <div>👇 Más abajo están, uno por uno, los <b>posteos analizados</b> (con link) y los <b>comentarios reales</b> con su etiqueta.</div>
            {Array.isArray(detalle?.notes) && detalle.notes.length ? (
              <div style={{ color: '#64748b', fontSize: 12, marginTop: 6 }}>ℹ️ {detalle.notes.join(' · ')}</div>
            ) : null}
          </div>
        ) : (
          <Empty>Todavía no corrió la medición diaria (9:00). El índice se calcula sobre comentarios reales de redes de los posteos que mencionan a Chouhy; apenas corra, acá vas a ver la fuente, los posteos y los comentarios analizados.</Empty>
        )}
      </TechSection>

      {stats?.resumen_ejecutivo ? (
        <TechSection title="Resumen ejecutivo" icon={<FaFileAlt />}>
          <div style={{ fontSize: 14, color: '#e2e8f0', lineHeight: 1.6 }}>{stats.resumen_ejecutivo}</div>
        </TechSection>
      ) : null}

      <TechSection title="Evolución del índice (90 días)" icon={<FaHeartbeat />}>
        {serie.length < 2 ? (
          <Empty>El índice se calcula todos los días a las 9:00. Con más días, acá vas a ver la curva de tendencia. {serie.length === 1 ? '(Ya hay 1 punto cargado.)' : ''}</Empty>
        ) : (
          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <LineChart data={serie} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="dia" stroke="#64748b" fontSize={11} />
                <YAxis domain={[0, 100]} stroke="#64748b" fontSize={11} />
                <ReferenceLine y={50} stroke="rgba(255,255,255,0.15)" strokeDasharray="4 4" />
                <Tooltip contentStyle={{ background: '#111c2b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="score" stroke="#00b4d8" strokeWidth={2} dot={{ r: 2 }} name="Índice" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </TechSection>

      <TechSection title="Cómo se compone el índice de hoy" icon={<FaBalanceScale />}>
        {brk ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
            <BreakTile label="Sentimiento (redes)" value={brk.sent_score} sub={`peso ${Math.round((brk.peso_sentimiento ?? 0.7) * 100)}%`} />
            <BreakTile label="Share of voice" value={`${brk.sov_pct ?? 0}%`} sub={`peso ${Math.round((brk.peso_sov ?? 0.3) * 100)}%`} />
            <BreakTile label="Índice compuesto" value={brk.score ?? actual?.score ?? '—'} sub="sentimiento×0.7 + SoV×0.3" />
            <BreakTile label="Fuente del dato" value={detalle.fuente === 'social' ? 'Redes' : 'Prensa (fallback)'} sub={`${detalle.comentarios_reales ?? 0} comentarios · ${detalle.posts_analizados ?? 0} posteos`} />
          </div>
        ) : <Empty>El desglose se genera con el snapshot diario.</Empty>}
      </TechSection>

      {stats?.temas?.length ? (
        <TechSection title="Temas más comentados" icon={<FaHashtag />}>
          <div style={{ marginBottom: 10, fontSize: 12, color: '#64748b' }}>Barra verde = el tema juega a favor · roja = en contra · amarilla = mixto.</div>
          {stats.temas.map((t, i) => (
            <Bar key={i} label={t.tema} value={t.menciones || 0} max={temasMax} color={leanColor(t.lean)} right={`${t.menciones || 0}`} />
          ))}
        </TechSection>
      ) : null}

      {(stats?.argumentos_favor?.length || stats?.argumentos_contra?.length) ? (
        <TechSection title="Qué dice cada bando" icon={<FaBullhorn />}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <ArgList title="Argumentos a favor" color="#22c55e" items={stats.argumentos_favor} />
            <ArgList title="Críticas en contra" color="#ef4444" items={stats.argumentos_contra} />
          </div>
        </TechSection>
      ) : null}

      {stats?.emociones?.length ? (
        <TechSection title="Emociones dominantes" icon={<FaTheaterMasks />}>
          {stats.emociones.map((e, i) => (
            <Bar key={i} label={e.emocion} value={e.pct || 0} max={100} color="#00b4d8" right={`${e.pct || 0}%`} />
          ))}
        </TechSection>
      ) : null}

      {stats?.insight_persuasion?.length ? (
        <TechSection title="Recomendaciones de comunicación (según lo que dice la gente)" icon={<FaHeart />}>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {stats.insight_persuasion.map((t, i) => <li key={i} style={{ fontSize: 13.5, color: '#e2e8f0', lineHeight: 1.5 }}>{t}</li>)}
          </ul>
        </TechSection>
      ) : null}

      <TechSection title={`Fuentes analizadas${fuentes.length ? ` (${fuentes.length} posteos)` : ''}`} icon={<FaLink />}>
        {fuentes.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {fuentes.map((f, i) => (
              <div key={i} style={{ border: `1px solid ${borderSoft}`, borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <a href={f.url} target="_blank" rel="noreferrer" style={{ color: accent, fontFamily: mono, fontSize: 12, wordBreak: 'break-all' }}>{f.url}</a>
                  <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    <span style={{ color: '#22c55e' }}>{f.pos ?? 0} ✓</span> · <span style={{ color: '#ef4444' }}>{f.neg ?? 0} ✗</span> · <span style={{ color: '#94a3b8' }}>{f.neu ?? 0} ·</span>
                    <span style={{ color: '#64748b' }}> {f.n ?? 0} coment.</span>
                  </span>
                </div>
                {f.caption ? <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>{f.caption}</div> : null}
              </div>
            ))}
          </div>
        ) : <Empty>Sin fuentes cargadas aún. Aparecen tras el próximo snapshot con datos de redes.</Empty>}
      </TechSection>

      <TechSection title={`Comentarios reales${comentarios.length ? ` (${comentarios.length})` : ''}`} icon={<FaComments />}>
        {comentarios.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 620, overflowY: 'auto' }}>
            {comentarios.map((c, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', border: `1px solid ${borderSoft}`, borderRadius: 8, padding: '8px 10px' }}>
                <SentTag s={c.s} />
                <span style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.4 }}>{c.t}</span>
              </div>
            ))}
          </div>
        ) : <Empty>No se trajeron comentarios en la última medición (posteos sin comentarios públicos disponibles). Cuando haya, se listan acá con su etiqueta a favor / en contra.</Empty>}
      </TechSection>
    </TechShell>
  );
}
